import { useUpdateEffect } from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { Address } from "viem";
import { useAccount, useDisconnect } from "wagmi";

import { referrerSessionKey } from "./constants";

import { AuthContext } from "@/hooks/useAuth";
import { useCompliance } from "@/hooks/useCompliance";
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from "@/lib/session-storage";
import { makeRequest } from "@/network/api";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const currentPath = useLocation().pathname;
  const { address } = useAccount();
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const queryClient = useQueryClient();
  const { disconnect } = useDisconnect();
  const [role, setRole] = useState<string | undefined>(undefined);
  const [searchParams] = useSearchParams();

  const { data: session, isLoading } = useQuery({
    queryKey: ["session", address],
    queryFn: async () => {
      const res = await makeRequest("/auth/checkSession").then((res) =>
        res.json(),
      );
      if (res.error) {
        throw new Error(res.error);
      }
      return res;
    },
    enabled: !!address,
  });

  // Persist referrer from URL to sessionStorage when it changes
  useEffect(() => {
    const referrerFromParams = searchParams.get("referrer");
    if (referrerFromParams) {
      setSessionStorageItem(referrerSessionKey, referrerFromParams);
    }
  }, [searchParams]);

  // Get referrer value (from URL params or sessionStorage)
  const referrerFromUrl = React.useMemo(() => {
    const referrerFromParams = searchParams.get("referrer");
    if (referrerFromParams) {
      return referrerFromParams;
    }
    return getSessionStorageItem(referrerSessionKey);
  }, [searchParams]);

  useEffect(() => {
    if (session) {
      setAuthStateFromLoginResponse(session);
    }
    // keeping this same as before
    // we will be updating the auth provider to listen to SIWE hooks
    // instead of inferring state based on session
    // session query doesn't always work b/c there are times where it doesn't update
    // such as when onboarding completion is toggled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const isAuthenticated = !!session;

  const authedViaLogin = !!session;

  const logout = useCallback(async () => {
    await makeRequest("/auth/logout", { method: "POST" });
    queryClient.invalidateQueries();
    disconnect();
  }, [queryClient, disconnect]);

  const reconnect = useCallback(async () => {
    await makeRequest("/auth/checkSession").then((res) => res.json());
  }, []);

  const { geoCompliance } = useCompliance();

  const setAuthStateFromLoginResponse = useCallback(
    async (resJson: {
      error: string;
      hasCompletedOnboarding: boolean;
      role?: string;
    }) => {
      try {
        if (resJson.error) {
          setHasCompletedOnboarding(false);
          setRole(undefined);
          throw new Error(resJson?.error ?? "Error with login response");
        }
        setHasCompletedOnboarding(resJson.hasCompletedOnboarding);
        setRole(resJson.role);
        if (
          !resJson.hasCompletedOnboarding &&
          !currentPath.startsWith("/onboarding")
        ) {
          navigate("/onboarding/welcome");
        }

        return !resJson.error;
      } catch (err) {
        console.error("Error setting auth state from login response", err);
        return false;
      }
    },
    [currentPath, navigate],
  );

  useUpdateEffect(() => {
    if (geoCompliance?.restrictAccess) {
      logout();
    }
  }, [geoCompliance?.restrictAccess, isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{
        address: address as Address,
        isAuthenticated,
        isLoading,
        authedViaLogin,
        logout: logout,
        hasCompletedOnboarding,
        reconnect,
        referrerFromUrl,
        role,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
